import { createFileRoute, Link } from '@tanstack/react-router'

// The page that the UptrackBot user agent points to (OpenSpec 128 follow-up).
// Every fact here comes from the code: the third party poller in uptrack-server
// (crates/jobs/src/third_party_poller.rs) and the check clients in
// uptrack-probe-rs and uptrack-worker. Change the page when they change.
export const Route = createFileRoute('/_site/bot')({
  component: BotPage,
  head: () => ({
    meta: [
      { title: 'UptrackBot and other Uptrack user agents — Uptrack' },
      {
        name: 'description',
        content:
          'What UptrackBot and Uptrack Monitor request, how often, the exact user agent strings, and how to report a problem or ask us to stop.',
      },
      { property: 'og:title', content: 'UptrackBot and other Uptrack user agents — Uptrack' },
      { property: 'og:description', content: 'What each Uptrack user agent requests, how often, and how to contact us.' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/bot' }],
  }),
})

const SUPPORT = 'mailto:support@uptrack.app?subject=Uptrack%20user%20agent'

function Code({ children }: { children: string }) {
  return <code className="font-mono text-sm text-foreground">{children}</code>
}

const MONITOR_AGENTS = [
  'Uptrack Monitor/1.0 (Rust)',
  'Uptrack Monitor/1.0 (Rust; sitemap)',
  'Uptrack Monitor/1.0 (Edge; +https://uptrack.app/docs/check-locations)',
  'Uptrack Monitor/1.0',
]

function BotPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <div className="mb-4">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          &larr; Back to home
        </Link>
      </div>
      <h1 className="mb-8 text-3xl font-semibold text-foreground">UptrackBot and other Uptrack user agents</h1>
      <div className="max-w-none space-y-8 text-body">
        <p className="text-sm text-muted-foreground">Last updated: October 14, 2026</p>

        <p>
          Uptrack is an uptime monitoring service. This page lists the user agents that Uptrack sends, what each one
          requests and how often. If one of them causes a problem on your site, email{' '}
          <a href={SUPPORT} className="text-primary hover:underline">
            support@uptrack.app
          </a>
          .
        </p>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-foreground">UptrackBot</h2>
          <p>
            <Code>UptrackBot/1.0 (+https://uptrack.app/bot)</Code>
          </p>
          <p>
            UptrackBot reads the public status data of services that Uptrack customers depend on, for example the JSON
            feed behind a public status page. It reads only URLs from a list that Uptrack keeps. A customer cannot make
            UptrackBot request a URL.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              It reads each service <span className="num">1</span> time per minute at most, however many customers watch
              it. When no customer watches a service, it stops.
            </li>
            <li>
              Each read is <span className="num">1</span> <Code>GET</Code> request with{' '}
              <Code>Accept: application/json</Code>. It sends <Code>If-None-Match</Code> with the last ETag, so an
              unchanged feed can answer <Code>304 Not Modified</Code>.
            </li>
            <li>
              It waits <span className="num">10</span> seconds at most, reads <span className="num">2</span> MB at most
              and does not follow redirects.
            </li>
            <li>
              After a failed read it waits longer: <span className="num">1, 2, 4, 8</span>, then{' '}
              <span className="num">10</span> minutes. If you answer <Code>429</Code> or <Code>503</Code> with a{' '}
              <Code>Retry-After</Code> header, it waits that long, up to <span className="num">1</span> hour.
            </li>
            <li>
              It does not crawl: it requests the known status URL and follows no links. It does not read{' '}
              <Code>robots.txt</Code>. To ask us to stop, email us (see the end of this page).
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-foreground">Uptrack Monitor</h2>
          <p>
            Uptrack Monitor checks the URLs that Uptrack customers add as monitors, for example a home page, an API
            health URL or a sitemap. It sends one of these user agents:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            {MONITOR_AGENTS.map((ua) => (
              <li key={ua}>
                <Code>{ua}</Code>
              </li>
            ))}
          </ul>
          <p>
            It checks at the interval that the customer chose, as often as every <span className="num">30</span>{' '}
            seconds, from one or more check locations. Each check requests the one URL of the monitor. It does not
            crawl the page or follow its links. The check locations are described in{' '}
            <Link to="/blog/22-check-locations" className="text-primary hover:underline">
              24 Check Locations Without 24 Times the False Alarms
            </Link>
            .
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-foreground">Other Uptrack user agents</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <Code>Uptrack Checker/1.0</Code> looks at a site&apos;s TLS certificate to explain a failed check: when
              someone uses the website check tool on uptrack.app, or when an Uptrack monitor fails.
            </li>
            <li>
              <Code>Uptrack-Webhook/1.0</Code> sends alert messages to a webhook URL that an Uptrack customer set up.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-foreground">For site owners</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              To let these requests through a firewall or bot filter, match the user agent{' '}
              <Code>UptrackBot</Code> or <Code>Uptrack Monitor</Code>.
            </li>
            <li>
              To slow UptrackBot down, answer <Code>429</Code> with a <Code>Retry-After</Code> header.
            </li>
            <li>
              To report a problem or ask us to stop, email{' '}
              <a href={SUPPORT} className="text-primary hover:underline">
                support@uptrack.app
              </a>
              . Tell us the URL, the user agent and the time of the requests, with the time zone.
            </li>
          </ul>
        </section>
      </div>
    </div>
  )
}
